import { useState } from 'react'
import { PiPlus } from 'react-icons/pi'
import Checkbox from 'components/Checkbox/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { cn } from 'components/UI/cn'
import { toMetricKind, toTableKind } from '../builder'
import { DEFAULT_VIEW_LIMIT } from '../engine'
import { frameOf } from '../frames'
import {
  applyGroupBy,
  categoricalFields,
  dateFields,
  dateFilterFields,
  dateFilterOf,
  hasOpenFilter,
  inFilterFields,
  inFilterValues,
  numericFields,
  numericFilterFields,
  numericFilterOf,
  removeDateFilter,
  removeInFilter,
  removeNumericFilter,
  setColumns,
  setCompareMeasure,
  setCumulative,
  setDateFilter,
  setInFilter,
  setPrimaryAgg,
  setNumericFilter,
  setOpenFilter,
  setSeriesBy,
  setSort,
  setTopN,
  sortableFields,
} from '../pipeline-ops'
import { AggFn } from '../pipeline-types'
import { ViewConfig } from '../view-config'
import { ColumnPicker } from './ColumnPicker'
import { MenuItem, MenuSection } from './FilterMenu'
import { FilterSelect } from './FilterSelect'
import { MatchedSelect } from './MatchedSelect'
import { ThresholdControl } from './ThresholdControl'
import { fieldLabelOf } from './ReportTable'
import {
  DateCompareFilterRow,
  NumericFilterRow,
  OpenDealsRow,
} from './ScalarFilters'

/**
 * Report builder sidebar controls. These own the report's *shape* (what the
 * data is): raw rows vs a summary, the measure, the breakdown, the filters.
 * How it's *drawn* (table / bar / line / ranked / number) is a separate,
 * non-destructive choice made by the "Show as" switcher above the preview
 * (see ViewSwitcher) — so a single metric is just a summary with no breakdown,
 * never a view toggle that magically collapses the report.
 *
 * Plain language only — no "frame / pipeline / aggregate" jargon. Every field
 * list (measures, breakdowns, columns, filters) comes from the config's frame.
 */

const NO_BREAKDOWN = '__none__'
const NO_SECOND_MEASURE = '__none__'

/**
 * Plain-language labels for the aggregation choice. "Total" reads better than
 * "Sum" for a non-technical builder; Count needs no field (it counts rows).
 */
const AGG_LABELS: Record<AggFn, string> = {
  count: 'Count',
  sum: 'Total',
  avg: 'Average',
  // Unique count of a field's values (e.g. # of distinct reps / accounts).
  distinct: 'Distinct count',
  // A running (cumulative) total — only offered over a time bucket. Sugar for a
  // Total whose values accumulate across periods (config.cumulative).
  cumsum: 'Running total',
  // A running (cumulative) count — count rows, accumulated across periods (e.g.
  // deals closed month over month). Sugar for Count + config.cumulative.
  cumcount: 'Running count',
}
// Numeric-measure aggs, in picker order. `distinct` is appended separately
// (it's gated on categorical dims, not numeric measures) and `cumsum` only over
// a time bucket.
const AGG_ORDER: AggFn[] = ['count', 'sum', 'avg']

/** Current groupBy as a select value (`field` or `field:bucket`). */
const breakdownValueOf = (config: ViewConfig): string => {
  if (!config.groupBy) return NO_BREAKDOWN
  return config.groupBy.bucket
    ? `${config.groupBy.field}:${config.groupBy.bucket}`
    : String(config.groupBy.field)
}

/** Decode a `field` / `field:bucket` select value. */
const parseBreakdown = (
  value: string
): { field: string; bucket?: 'month' | 'quarter' } => {
  const [field, bucket] = value.split(':')
  return { field, bucket: bucket as 'month' | 'quarter' | undefined }
}

/**
 * "Show top N" limit for a categorical breakdown — keep only the leading groups
 * (e.g. top 10 owners by revenue). Hidden for time buckets, where the order is
 * chronological and slicing would just drop early periods.
 */
const TOP_N_PRESETS = [0, 5, 10, 15, 20]
function TopNField({
  config,
  onChange,
}: {
  config: ViewConfig
  onChange: (next: ViewConfig) => void
}) {
  const topLabel = (n: number) => (n === 0 ? 'All groups' : `Top ${n}`)
  return (
    <Field label="Show">
      <MatchedSelect
        ariaLabel="Show top N"
        options={TOP_N_PRESETS.map((n) => ({
          value: String(n),
          label: topLabel(n),
        }))}
        value={{
          value: String(config.limit ?? DEFAULT_VIEW_LIMIT),
          label: topLabel(config.limit ?? DEFAULT_VIEW_LIMIT),
        }}
        onChange={(v) => onChange(setTopN(config, Number(v)))}
      />
    </Field>
  )
}

/** Small labelled wrapper matching the dropdown label style. */
function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col">
      <span className="mb-1 text-xs text-fg-secondary">{label}</span>
      {children}
    </div>
  )
}

/** Two-option segmented toggle (pill style), matching the "Show as" switcher. */
function Segmented<T extends string>({
  ariaLabel,
  options,
  value,
  onChange,
}: {
  ariaLabel: string
  options: { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="flex w-full gap-1 rounded-lg bg-surface-sunken p-1"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            // Equal-width columns so every option (incl. a 4th like Pivot) fits
            // one row inside the sidebar instead of overflowing.
            'flex-1 whitespace-nowrap rounded-md px-2 py-1.5 text-center text-sm font-medium transition-colors',
            value === o.value
              ? 'bg-surface text-fg shadow-sm'
              : 'text-fg-secondary hover:text-fg'
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** Operations + filters for the report, stacked for the right sidebar. */
export function ReportControls({
  config,
  onChange,
}: {
  config: ViewConfig
  onChange: (next: ViewConfig) => void
}) {
  const frame = frameOf(config.frame)
  // Controls the "Add filter" menu so picking an option closes it (a Radix
  // Popover doesn't auto-close on a click inside its own content).
  const [addFilterOpen, setAddFilterOpen] = useState(false)
  const pickFilter = (next: ViewConfig) => {
    onChange(next)
    setAddFilterOpen(false)
  }
  const fieldLabel = (f: string): string => fieldLabelOf(frame, f)
  const distinctValues = (f: string): string[] =>
    (frame.fields.find((x) => x.name === f)?.values ?? []).map(String).sort()

  // Breakdown options: the frame's categorical dims plus a time bucket on each
  // of its date fields, encoded `field:bucket` so one select drives both.
  const timeLabel = (field: string, bucket: string): string =>
    `${fieldLabel(field)} · by ${bucket}`
  const breakdownOptions = [
    ...frame.catDims.map((f) => ({ value: f, label: fieldLabel(f) })),
    ...frame.dateFields.flatMap((field) =>
      (['month', 'quarter'] as const).map((bucket) => ({
        value: `${field}:${bucket}`,
        label: timeLabel(field, bucket),
      }))
    ),
  ]
  const breakdownLabelOf = (value: string): string =>
    breakdownOptions.find((o) => o.value === value)?.label ?? value

  const measures = frame.measures

  // Shape: a raw list (Table), a single-metric Summary, a 1D Group by, or a 2D
  // Pivot (rows × columns). These are the engine shapes, made explicit. A Pivot
  // is its own shape — chosen deliberately, never a split bolted onto a Group by
  // (which collapses the rows the split would need).
  const shape: 'rows' | 'summary' | 'breakdown' | 'pivot' =
    config.kind === 'list'
      ? 'rows'
      : !config.groupBy
        ? 'summary'
        : config.seriesBy
          ? 'pivot'
          : 'breakdown'
  const isCategorical = !!config.groupBy && !config.groupBy.bucket
  // A Pivot needs a categorical column to split on plus a second dim (categorical
  // or date) for the row axis — else it's offered nowhere.
  const canPivot =
    frame.catDims.length >= 1 &&
    frame.catDims.length + frame.dateFields.length >= 2
  // The column split a fresh Pivot defaults to: the first categorical dim that
  // isn't the row field.
  const defaultSplit = (rowField: string): string | null =>
    frame.catDims.find((f) => f !== rowField) ?? null

  // The primary measure is an explicit aggregation + field pair. "Of" fields are
  // the frame's curated measure fields (distinct, in frame order); Count needs
  // no field, so a count-only frame (e.g. activity) offers Count alone.
  const primary = config.metrics?.[0] ?? measures[0]
  const measure = primary.label
  const measureFields = measures.reduce<string[]>((acc, m) => {
    if (m.field && !acc.includes(m.field)) acc.push(m.field)
    return acc
  }, [])
  // A running total reads as the agg choice "cumsum" — it's really a Total whose
  // per-period values accumulate (config.cumulative), and only valid over a time
  // bucket, so it's offered (and reflected) only then.
  const overTime = !!config.groupBy?.bucket
  // Over a time bucket, a cumulative metric reads as a running agg: count → a
  // Running count, anything else → a Running total.
  const aggValue: AggFn =
    overTime && config.cumulative
      ? primary.agg === 'count'
        ? 'cumcount'
        : 'cumsum'
      : primary.agg
  // Distinct counts a column's unique values — a measure, not a breakdown — so
  // its "Of" options are *every* categorical column (e.g. Company), not just the
  // low-cardinality breakdown dims (catDims) you'd group/pivot by. Numeric
  // measure fields are what sum/avg roll up instead.
  const distinctFields = frame.fields
    .filter((f) => f.type === 'cat')
    .map((f) => f.name)
  const ofFields = aggValue === 'distinct' ? distinctFields : measureFields
  const fieldValue = primary.field ?? ofFields[0]
  const aggChoices: AggFn[] = measureFields.length ? [...AGG_ORDER] : ['count']
  if (distinctFields.length) aggChoices.push('distinct')
  // Running count needs no measure field (it counts rows), so it's offered over
  // any time bucket; running total needs a numeric measure to accumulate.
  if (overTime) aggChoices.push('cumcount')
  if (overTime && measureFields.length) aggChoices.push('cumsum')
  const aggOptions = aggChoices.map((a) => ({ value: a, label: AGG_LABELS[a] }))
  // Apply an agg-picker choice: cum* are sum/count + the cumulative flag (any
  // other choice clears it); distinct needs a categorical field, so seed one when
  // the current measure field isn't a category dim. Threads through both selects.
  const applyAgg = (choice: AggFn, field: string): ViewConfig => {
    if (choice === 'cumsum') {
      return setCumulative(setPrimaryAgg(config, 'sum', field), true)
    }
    if (choice === 'cumcount') {
      return setCumulative(setPrimaryAgg(config, 'count', field), true)
    }
    const f =
      choice === 'distinct' && !distinctFields.includes(field)
        ? distinctFields[0]
        : field
    return setCumulative(setPrimaryAgg(config, choice, f), false)
  }
  const secondMeasure = config.chartMetric2

  const filterFields = inFilterFields(config)
  const numFields = numericFilterFields(config)
  const activeDateCompareFields = dateFilterFields(config)
  const openOn = hasOpenFilter(config)

  const unusedFilterFields = categoricalFields(config).filter(
    (f) => !filterFields.includes(f)
  )
  const unusedNumericFields = numericFields(config).filter(
    (f) => !numFields.includes(f)
  )
  const unusedDateFields = dateFields(config).filter(
    (f) => !activeDateCompareFields.includes(f)
  )
  // The open/closed toggle only makes sense for a frame with a close date.
  const hasCloseDate = frame.fields.some((f) => f.name === 'datetime_closed')
  const hasAnyFilter =
    config.filters.length > 0 ||
    filterFields.length > 0 ||
    numFields.length > 0 ||
    activeDateCompareFields.length > 0 ||
    openOn
  const canAddFilter =
    unusedFilterFields.length > 0 ||
    unusedNumericFields.length > 0 ||
    unusedDateFields.length > 0 ||
    (hasCloseDate && !openOn)

  // In the builder all filters are user-added, so clearing means none.
  const clearAllFilters = () => onChange({ ...config, filters: [] })

  return (
    <div className="flex flex-col gap-3">
      {/* Two axes, not four peers. Type is the binary: raw rows (Table) vs an
          aggregation. A Table isn't a fourth aggregation — it's the absence of
          one. */}
      <Field label="Type">
        <Segmented
          ariaLabel="Report type"
          options={[
            { value: 'rows', label: 'Table' },
            { value: 'aggregate', label: 'Aggregate' },
          ]}
          value={shape === 'rows' ? 'rows' : 'aggregate'}
          onChange={(v) => {
            if (v === 'rows') {
              // `toTableKind` drops any goal as an invariant (a row Table can't
              // grade), so nothing extra to strip here.
              if (shape !== 'rows') onChange(toTableKind(config))
              return
            }
            // Table → Aggregate: open on the simplest aggregation, a Summary
            // (one number); the Breakdown control below adds dimensions.
            if (shape === 'rows') onChange(toMetricKind(config))
          }}
        />
      </Field>

      {/* The three aggregations, by dimension count: Summary = one number (0
          dims), Group by = rows (1 dim), Pivot = rows × columns (2 dims). */}
      {shape !== 'rows' && (
        <Field label="Breakdown">
          <Segmented
            ariaLabel="Aggregation breakdown"
            options={[
              { value: 'summary', label: 'Summary' },
              { value: 'breakdown', label: 'Group by' },
              ...(canPivot
                ? [{ value: 'pivot' as const, label: 'Pivot' }]
                : []),
            ]}
            value={shape}
            onChange={(v) => {
              if (v === shape) return
              // A goal carries across these (Summary number and grouped chart can
              // both show it), so we never strip it on a breakdown change.
              if (v === 'summary') return onChange(toMetricKind(config))
              // Group by → 1D grouped; seed a default field if there isn't one.
              // applyGroupBy clears any series, so leaving a Pivot drops its split.
              const rowField = config.groupBy?.field ?? frame.catDims[0]
              const grouped = applyGroupBy(
                config,
                rowField,
                config.groupBy?.bucket
              )
              if (v === 'breakdown') return onChange(grouped)
              // Pivot → 2D; keep the existing split if it isn't the row field,
              // else seed a default column. Default the drawing to a stacked bar.
              const split =
                config.seriesBy && config.seriesBy !== rowField
                  ? config.seriesBy
                  : defaultSplit(rowField)
              onChange(
                split
                  ? { ...setSeriesBy(grouped, split), chart: 'stacked-bar' }
                  : grouped
              )
            }}
          />
        </Field>
      )}

      {shape === 'rows' ? (
        // Raw rows: pick columns + how to order them.
        <>
          <Field label="Columns">
            <ColumnPicker
              label=""
              idPrefix="setup-columns"
              allFields={frame.columns.map((f) => ({
                value: f,
                label: fieldLabel(f),
              }))}
              selected={config.columns ?? frame.columns}
              onChange={(next) => onChange(setColumns(config, next))}
            />
          </Field>
          <Field label="Sort by">
            <MatchedSelect
              ariaLabel="Sort by"
              options={sortableFields(config).map((f) => ({
                value: f,
                label: fieldLabel(f),
              }))}
              value={{
                value: config.sort?.by ?? sortableFields(config)[0],
                label: fieldLabel(config.sort?.by ?? sortableFields(config)[0]),
              }}
              onChange={(v) =>
                onChange(setSort(config, v, config.sort?.dir ?? 'desc'))
              }
            />
          </Field>
          <Field label="Order">
            <MatchedSelect
              ariaLabel="Sort order"
              options={[
                { value: 'desc', label: 'High → low' },
                { value: 'asc', label: 'Low → high' },
              ]}
              value={{
                value: config.sort?.dir ?? 'desc',
                label:
                  (config.sort?.dir ?? 'desc') === 'asc'
                    ? 'Low → high'
                    : 'High → low',
              }}
              onChange={(dir) =>
                onChange(
                  setSort(
                    config,
                    config.sort?.by ?? sortableFields(config)[0],
                    dir as 'asc' | 'desc'
                  )
                )
              }
            />
          </Field>
        </>
      ) : (
        // Summary: aggregation + field + breakdown. No breakdown → a number.
        <>
          <Field label="Measure">
            <MatchedSelect
              ariaLabel="Aggregation"
              options={aggOptions}
              value={{ value: aggValue, label: AGG_LABELS[aggValue] }}
              onChange={(v) => onChange(applyAgg(v as AggFn, fieldValue))}
            />
          </Field>
          {aggValue !== 'count' && aggValue !== 'cumcount' && (
            <Field label="Of">
              <MatchedSelect
                ariaLabel="Measure field"
                options={ofFields.map((f) => ({
                  value: f,
                  label: fieldLabel(f),
                }))}
                value={{ value: fieldValue, label: fieldLabel(fieldValue) }}
                onChange={(v) => onChange(applyAgg(aggValue, v))}
              />
            </Field>
          )}
          {(shape === 'breakdown' || shape === 'pivot') && (
            <>
              <Field label={shape === 'pivot' ? 'Rows' : 'Group by'}>
                <MatchedSelect
                  ariaLabel={shape === 'pivot' ? 'Rows' : 'Group by'}
                  options={breakdownOptions}
                  value={{
                    value: breakdownValueOf(config),
                    label: breakdownLabelOf(breakdownValueOf(config)),
                  }}
                  onChange={(v) => {
                    const { field, bucket } = parseBreakdown(v)
                    const grouped = applyGroupBy(config, field, bucket)
                    // Under a Pivot, applyGroupBy clears the split — re-apply it
                    // (or a fresh default when the new row field was the split).
                    if (shape === 'pivot') {
                      const split =
                        config.seriesBy && config.seriesBy !== field
                          ? config.seriesBy
                          : defaultSplit(field)
                      return onChange(
                        split ? setSeriesBy(grouped, split) : grouped
                      )
                    }
                    onChange(grouped)
                  }}
                />
              </Field>
              {/* Columns — the split: one stacked/grouped bar segment (or, over a
                  time row axis, one line) per value. Categorical only; never the
                  row field. Required (a Pivot is 2D by definition). */}
              {shape === 'pivot' && (
                <Field label="Columns">
                  <MatchedSelect
                    ariaLabel="Columns (split into series)"
                    options={frame.catDims
                      .filter((f) => f !== config.groupBy?.field)
                      .map((f) => ({ value: f, label: fieldLabel(f) }))}
                    value={{
                      value: config.seriesBy ?? '',
                      label: config.seriesBy ? fieldLabel(config.seriesBy) : '',
                    }}
                    onChange={(v) => onChange(setSeriesBy(config, v))}
                  />
                </Field>
              )}
              {/* A dual-measure combo can't coexist with a series split, so it's
                  a Group-by-only option. */}
              {shape === 'breakdown' && (
                <Field label="Compare with">
                  <MatchedSelect
                    ariaLabel="Compare with a second measure"
                    options={[
                      { value: NO_SECOND_MEASURE, label: 'No second measure' },
                      ...measures
                        .filter((m) => m.label !== measure)
                        .map((m) => ({ value: m.label, label: m.label })),
                    ]}
                    value={{
                      value: secondMeasure ?? NO_SECOND_MEASURE,
                      label: secondMeasure ?? 'No second measure',
                    }}
                    onChange={(v) =>
                      onChange(
                        setCompareMeasure(
                          config,
                          v === NO_SECOND_MEASURE ? null : v
                        )
                      )
                    }
                  />
                </Field>
              )}
              {isCategorical && (
                <TopNField config={config} onChange={onChange} />
              )}
            </>
          )}
          {/* Goal — a chart reference line, or a met/missed badge on a number. */}
          <ThresholdControl config={config} onChange={onChange} />
          {shape === 'summary' && (
            // Single-number extras: period comparison + trend line + goal bar.
            <>
              <Field label="Time period">
                <MatchedSelect
                  ariaLabel="Time period for this metric"
                  options={[
                    { value: 'none', label: 'All time' },
                    { value: 'month', label: 'This month' },
                    { value: 'quarter', label: 'This quarter' },
                    { value: 'year', label: 'This year' },
                  ]}
                  value={{
                    value: config.trend ?? 'none',
                    label:
                      config.trend === 'month'
                        ? 'This month'
                        : config.trend === 'quarter'
                          ? 'This quarter'
                          : config.trend === 'year'
                            ? 'This year'
                            : 'All time',
                  }}
                  onChange={(v) =>
                    onChange({
                      ...config,
                      trend:
                        v === 'none'
                          ? undefined
                          : (v as 'month' | 'quarter' | 'year'),
                    })
                  }
                />
              </Field>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-fg">
                <Checkbox
                  boxSize="sm"
                  checked={!!config.sparkline}
                  onChange={() =>
                    onChange({ ...config, sparkline: !config.sparkline })
                  }
                />
                Trend line
              </label>
            </>
          )}
        </>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-2 border-t border-line pt-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-fg-secondary">
            Filters
          </span>
          {hasAnyFilter && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="text-xs text-fg-secondary hover:text-fg"
            >
              Clear all
            </button>
          )}
        </div>

        {/* Categorical (multi-select) filters */}
        {filterFields.map((field) => (
          <FilterSelect
            key={field}
            label={fieldLabel(field)}
            idPrefix={`setup-filter-${field}`}
            options={distinctValues(field).map((v) => ({ value: v, label: v }))}
            selected={inFilterValues(config, field)}
            onChange={(next) => onChange(setInFilter(config, field, next))}
            onRemove={() => onChange(removeInFilter(config, field))}
          />
        ))}

        {/* Numeric threshold filters */}
        {numFields.map((field) => {
          const cond = numericFilterOf(config, field)
          return (
            <NumericFilterRow
              key={field}
              label={fieldLabel(field)}
              ariaLabel={fieldLabel(field)}
              op={cond?.op ?? 'gte'}
              value={cond?.value ?? 0}
              onChange={(op, value) =>
                onChange(setNumericFilter(config, field, op, value))
              }
              onRemove={() => onChange(removeNumericFilter(config, field))}
            />
          )
        })}

        {/* Absolute date filters (on or after / on or before a date) */}
        {activeDateCompareFields.map((field) => {
          const cond = dateFilterOf(config, field)
          return (
            <DateCompareFilterRow
              key={field}
              label={fieldLabel(field)}
              ariaLabel={fieldLabel(field)}
              op={cond?.op ?? 'date_after'}
              value={cond?.value ?? ''}
              onChange={(op, value) =>
                onChange(setDateFilter(config, field, op, value))
              }
              onRemove={() => onChange(removeDateFilter(config, field))}
            />
          )
        })}

        {/* Open-deals toggle */}
        {openOn && (
          <OpenDealsRow
            onRemove={() => onChange(setOpenFilter(config, false))}
          />
        )}

        {canAddFilter && (
          <Popover open={addFilterOpen} onOpenChange={setAddFilterOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex w-fit items-center gap-1 rounded-md border border-dashed border-line px-3 py-2 text-sm text-fg-secondary transition-colors hover:border-brand-active hover:text-fg"
              >
                <PiPlus size={14} /> Add filter
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-56 p-1">
              {unusedFilterFields.length > 0 && (
                <MenuSection label="Categories">
                  {unusedFilterFields.map((f) => (
                    <MenuItem
                      key={f}
                      label={fieldLabel(f)}
                      onClick={() =>
                        pickFilter(setInFilter(config, f, distinctValues(f)))
                      }
                    />
                  ))}
                </MenuSection>
              )}
              {unusedNumericFields.length > 0 && (
                <MenuSection label="Numbers">
                  {unusedNumericFields.map((f) => (
                    <MenuItem
                      key={f}
                      label={fieldLabel(f)}
                      onClick={() =>
                        pickFilter(setNumericFilter(config, f, 'gte', 0))
                      }
                    />
                  ))}
                </MenuSection>
              )}
              {unusedDateFields.length > 0 && (
                <MenuSection label="Dates">
                  {unusedDateFields.map((f) => (
                    <MenuItem
                      key={f}
                      label={fieldLabel(f)}
                      // Seed an "on or after the start of this year" bound so the
                      // row renders with a value to adjust; the operator and date
                      // are then editable inline.
                      onClick={() =>
                        pickFilter(
                          setDateFilter(
                            config,
                            f,
                            'date_after',
                            `${new Date().getFullYear()}-01-01`
                          )
                        )
                      }
                    />
                  ))}
                </MenuSection>
              )}
              {hasCloseDate && !openOn && (
                <MenuSection label="Status">
                  <MenuItem
                    label="Open deals only"
                    onClick={() => pickFilter(setOpenFilter(config, true))}
                  />
                </MenuSection>
              )}
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  )
}
